import { useEffect, useRef, useState } from "react";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { fmtMinutes, lengthOutOfRange } from "../utils/pricing";

export const TARGET_MIN = 5;
export const TARGET_MAX = 15;
export const TARGET_DEFAULT = 5;

const clampStep = (n) => Math.min(TARGET_MAX, Math.max(TARGET_MIN, Math.round(n * 2) / 2));

// Slider + number input kept in sync both ways; parent save is debounced.
// Red only for a complete value outside 5–15; clamps on blur.
export const TargetLengthControl = ({ value, onChange, debounceMs = 600, testIdPrefix = "target-length" }) => {
    const [num, setNum] = useState(clampStep(Number(value) || TARGET_DEFAULT));
    const [raw, setRaw] = useState(String(num));
    const timer = useRef(null);
    const lastSent = useRef(num);

    useEffect(() => {
        const v = clampStep(Number(value) || TARGET_DEFAULT);
        if (v !== lastSent.current) {
            lastSent.current = v;
            setNum(v);
            setRaw(String(v));
        }
    }, [value]);

    useEffect(() => () => clearTimeout(timer.current), []);

    const schedule = (v) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            if (v !== lastSent.current) {
                lastSent.current = v;
                onChange(v);
            }
        }, debounceMs);
    };

    const parsed = raw.trim() === "" || raw.endsWith(".") ? NaN : Number(raw);
    const outOfRange = !Number.isNaN(parsed) && (parsed < TARGET_MIN || parsed > TARGET_MAX);

    const onType = (e) => {
        const s = e.target.value.replace(/[^\d.]/g, "");
        setRaw(s);
        const n = Number(s);
        if (s && !s.endsWith(".") && !Number.isNaN(n) && n >= TARGET_MIN && n <= TARGET_MAX) {
            const v = clampStep(n);
            setNum(v);
            schedule(v);
        }
    };
    const onBlur = () => {
        const n = Number(raw);
        const v = Number.isNaN(n) || raw.trim() === "" ? num : clampStep(n);
        setNum(v);
        setRaw(String(v));
        schedule(v);
    };
    const onSlide = ([v]) => {
        setNum(v);
        setRaw(String(v));
        schedule(v);
    };

    return (
        <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-control`}>
            <Slider
                min={TARGET_MIN}
                max={TARGET_MAX}
                step={0.5}
                value={[num]}
                onValueChange={onSlide}
                className="flex-1"
                data-testid={`${testIdPrefix}-slider`}
            />
            <Input
                type="text"
                inputMode="decimal"
                value={raw}
                onChange={onType}
                onBlur={onBlur}
                aria-invalid={outOfRange}
                className={`w-20 text-right mono ${outOfRange ? "border-red-500 text-red-600 focus-visible:ring-red-500" : ""}`}
                data-testid={`${testIdPrefix}-input`}
            />
            <span className="text-xs text-neutral-500">min</span>
            {outOfRange && (
                <span className="text-[11px] text-red-600" data-testid={`${testIdPrefix}-error`}>
                    {TARGET_MIN}–{TARGET_MAX} min
                </span>
            )}
        </div>
    );
};

export const TargetVsCurrent = ({ targetMinutes, estimatedSeconds, testId = "target-vs-current" }) => (
    <span className={`mono text-[12px] transition-colors ${lengthOutOfRange(estimatedSeconds) ? "text-red-600" : "text-neutral-700"}`}
        data-testid={testId}>
        Target {Number(targetMinutes || TARGET_DEFAULT).toFixed(1)} min / Current {fmtMinutes(estimatedSeconds)} min
    </span>
);

export default TargetLengthControl;
